// Velocity animation configuration for screen transitions.
//
// We have 'display: none' on each previous screen animation so it
// disappears when the animation is complete.
//
// Where a property is an array the meaning is:
//
//   property: [<final value>, <initial value>]
//
// Note the comments where the name of the transition in the Editor UI
// does not match the transition name stored in the database!
//
import { defaults, forEach } from 'lodash-es';
import { velocityHelpers } from 'velocity-react';
var pushEasing = [0.272, 0.224, 0.265, 0.975];
var flowScale = 0.7;
var flowOutToLeftFrames = [
    [
        {
            scale: flowScale,
        },
        0.6,
        {
            easing: 'ease',
        },
    ],
    [
        {
            scale: flowScale,
        },
        0.1,
        {
            easing: 'ease',
        },
    ],
    [
        {
            translateX: "-".concat(100 / flowScale, "%"),
        },
        0.3,
        {
            easing: 'ease',
        },
    ],
];
var flowInFromRightFrames = [
    [
        {
            translateX: [0, '100%'],
            scale: [flowScale, flowScale],
        },
        0.3,
        {
            easing: 'ease',
        },
    ],
    [
        {
            translateX: 0,
        },
        0.1,
        {
            easing: 'ease',
        },
    ],
    [
        {
            scale: 1,
        },
        0.6,
        {
            easing: 'ease',
        },
    ],
];
var flowInFromLeftFrames = [
    [
        {
            translateX: [0, "-".concat(100 / flowScale, "%")],
            scale: [flowScale, flowScale],
        },
        0.3,
        {
            easing: 'ease',
        },
    ],
    // TODO better way to specify pause than 'noop' animation?
    [
        {
            translateX: 0,
        },
        0.1,
        {
            easing: 'ease',
        },
    ],
    [
        {
            scale: 1,
        },
        0.6,
        {
            easing: 'ease',
        },
    ],
];
var flowOutToRightFrames = [
    [
        {
            scale: flowScale,
        },
        0.6,
        {
            easing: 'ease',
        },
    ],
    // TODO better way to specify pause than 'noop' animation?
    [
        {
            scale: flowScale,
        },
        0.1,
        {
            easing: 'ease',
        },
    ],
    [
        {
            translateX: "".concat(100 / flowScale, "%"),
        },
        0.3,
        {
            easing: 'ease',
        },
    ],
];
var flowOutToLeft = velocityHelpers.registerEffect({
    calls: flowOutToLeftFrames,
});
var flowInFromRight = velocityHelpers.registerEffect({
    calls: flowInFromRightFrames,
});
var flowOutToRight = velocityHelpers.registerEffect({
    calls: flowOutToRightFrames,
});
var flowInFromLeft = velocityHelpers.registerEffect({
    calls: flowInFromLeftFrames,
});
// Define animations as
//
// {previous,current}Screen:
// [ <CSS properties to animate>, <animation options> ]
//
var transitions = {
    fade: {
        currentScreen: [{ opacity: [1, 0] }],
    },
    // This is 'push left' in the Editor UI
    pushright: {
        previousScreen: [
            {
                translateX: ['-30%', pushEasing, 0],
                opacity: [0.75, pushEasing],
            },
            { duration: 420 },
        ],
        currentScreen: [
            {
                translateX: ['0', pushEasing, '100%'],
            },
            { duration: 420 },
        ],
    },
    // This is 'push right' in the Editor UI
    pushleft: {
        previousScreen: [
            {
                translateX: ['30%', pushEasing, 0],
                opacity: [0.75, pushEasing],
            },
            { duration: 420 },
        ],
        currentScreen: [
            {
                translateX: [0, pushEasing, '-100%'],
            },
            { duration: 420 },
        ],
    },
    // This is 'slide left' in the Editor UI
    slide: {
        previousScreen: [
            {
                translateX: ['-100%', 'ease-in-out', 0],
            },
            { duration: 350 },
        ],
        currentScreen: [
            {
                translateX: ['0', 'ease-in-out', '100%'],
            },
            { duration: 350 },
        ],
    },
    // This is 'slide right' in the Editor UI
    'slide-left': {
        previousScreen: [
            {
                translateX: ['100%', 'ease-in-out', 0],
            },
        ],
        currentScreen: [
            {
                translateX: [0, 'ease-in-out', '-100%'],
            },
        ],
    },
    slideup: {
        previousScreen: null,
        currentScreen: [
            {
                translateY: [0, pushEasing, '100%'],
            },
            { duration: 450 },
        ],
    },
    slidedown: {
        previousScreen: [
            {
                translateY: ['100%', pushEasing, 0],
            },
            { duration: 450 },
        ],
        currentScreen: null,
    },
    pop: {
        previousScreen: [
            {
                scale: [0, 1],
                opacity: [0, 1],
            },
            { duration: 300 },
        ],
        currentScreen: [
            {
                scale: [1, 0],
                opacity: [1, 0],
            },
            { duration: 400, delay: 100 },
        ],
    },
    flip: {
        previousScreen: [
            {
                rotateY: ['-180deg', 'ease-in-out', 0],
                opacity: [0, 1],
            },
            { duration: 400 },
        ],
        currentScreen: [
            {
                rotateY: [0, 'ease-in-out', '180deg'],
                opacity: [1, 0],
            },
            { duration: 400 },
        ],
    },
    flow: {
        previousScreen: [flowOutToLeft, { duration: 600 }],
        currentScreen: [flowInFromRight, { duration: 800, delay: 600 }],
    },
    flowReverse: {
        previousScreen: [flowOutToRight, { duration: 600 }],
        currentScreen: [flowInFromLeft, { duration: 800, delay: 600 }],
    },
    slidefade: {
        previousScreen: [{ translateX: ['-100%', 0] }, { duration: 400 }],
        currentScreen: [{ opacity: [1, 0] }, { duration: 400 }],
    },
    slidefadeReverse: {
        previousScreen: [{ opacity: [0, 1] }, { duration: 400 }],
        currentScreen: [{ translateX: [0, '-100%'] }, { duration: 400 }],
    },
};
var defaultPreviousScreenOpts = {
    display: 'none',
    duration: 400,
};
var defaultCurrentScreenOpts = {
    display: 'block',
    duration: 400,
};
function assignDefaultScreenOptions(screen, defaultOpts) {
    /* eslint-disable no-param-reassign */
    if (screen) {
        var opts = screen[1] || {};
        defaults(opts, defaultOpts);
        screen[1] = opts;
    }
}
forEach(transitions, function (_a) {
    var previousScreen = _a.previousScreen, currentScreen = _a.currentScreen;
    assignDefaultScreenOptions(previousScreen, defaultPreviousScreenOpts);
    assignDefaultScreenOptions(currentScreen, defaultCurrentScreenOpts);
});
export default transitions;
export var reverseTransitions = {
    fade: 'fade',
    pushright: 'pushleft',
    pushleft: 'pushright',
    slide: 'slide-left',
    'slide-left': 'slide',
    slideup: 'slidedown',
    slidedown: 'slideup',
    pop: 'pop',
    flip: 'flip',
    flow: 'flowReverse',
    slidefade: 'slidefadeReverse',
};
